import * as Dialog from '@radix-ui/react-dialog';
import { type CSSProperties, createContext, type PointerEvent, type ReactElement, type ReactNode, type RefObject, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { MEDIA } from '../app/breakpoints.ts';
import { tokenMs } from '../app/motion.ts';
import { useOverlay } from '../app/router.tsx';
import { Icon, InstallIcon } from '../components/icons.tsx';
import { prefersReducedMotion } from '../game/haptics.ts';
import { uiPop } from '../game/sound.ts';
import { useDialogReturnFocus, useModalBackground } from './focus.ts';
import { useLayer } from './layers.ts';
import { SheetSay } from './Toast.tsx';
import './ui.css';

/** The sheet's way out: the reverse of its way in (ui.css `sheet-in`), quicker (--dur-sheet-out). */
const exitMs = () => tokenMs('--dur-sheet-out', 160);

/**
 * Closes a sheet the way it came: it drops and fades (from wherever a drag left it) with
 * its dim, then `done` unmounts it. Reduced motion, or no Web Animations: at once.
 */
export function sheetExit(el: HTMLElement | null, done: () => void): void {
	if (!el || typeof el.animate !== 'function' || prefersReducedMotion()) return done();
	if (el.dataset.leaving) return;
	el.dataset.leaving = '1';
	const ms = exitMs();
	// A popover (J4) lifts back towards ≡ instead of dropping.
	const to = el.closest('.sheet-wrap.pop') ? 'translateY(-6px)' : 'translateY(48px)';
	const from = el.style.transform || 'none';
	el.animate(
		[
			{ transform: from, opacity: 1 },
			{ transform: to, opacity: 0 },
		],
		{ duration: ms, easing: 'ease-in', fill: 'forwards' },
	);
	el.parentElement?.querySelector('.sheet-dim')?.animate([{ opacity: 1 }, { opacity: 0 }], {
		duration: ms,
		fill: 'forwards',
	});
	window.setTimeout(done, ms - 10);
}

/** How far (px) a sheet is pulled down before letting go closes it. */
const DRAG_CLOSE = 80;

/**
 * The handle means it: drag a sheet down by its top (the handle and the heading row) and
 * it follows the finger; let go past `DRAG_CLOSE` and it closes, else it settles back.
 * Buttons in that row keep their taps. Reduced motion: no settle animation.
 */
export function useSheetDrag(ref: RefObject<HTMLElement | null>, onClose: () => void) {
	const start = useRef<{ y: number; dy: number } | null>(null);
	const set = (dy: number, settle: boolean) => {
		const el = ref.current;
		if (!el) return;
		el.style.transition = settle ? 'transform 200ms var(--ease-out)' : 'none';
		el.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
	};
	return {
		onPointerDown(e: PointerEvent<HTMLElement>) {
			if (e.button !== 0 || (e.target as Element).closest('button, a')) return;
			start.current = { y: e.clientY, dy: 0 };
			e.currentTarget.setPointerCapture?.(e.pointerId);
		},
		onPointerMove(e: PointerEvent<HTMLElement>) {
			const s = start.current;
			if (!s) return;
			s.dy = Math.max(0, e.clientY - s.y);
			set(s.dy, false);
		},
		onPointerUp() {
			const s = start.current;
			start.current = null;
			if (!s) return;
			if (s.dy > DRAG_CLOSE) onClose();
			else set(0, true);
		},
		onPointerCancel() {
			start.current = null;
			set(0, true);
		},
	};
}

/** Where a sheet with an anchor hangs as a popover (J4): from 960px with a mouse. Phones and touch keep the sheet. */
export const POPOVER_MEDIA = `${MEDIA.wide} and ${MEDIA.finePointer}`;

interface Place {
	/** The anchor's bottom edge, from the top of the window. */
	top: number;
	/** The anchor's right edge, from the right of the window. */
	right: number;
}

function placeUnder(anchor: (() => HTMLElement | null) | undefined): Place | null {
	if (!anchor || typeof window === 'undefined' || !window.matchMedia?.(POPOVER_MEDIA).matches) return null;
	const r = anchor()?.getBoundingClientRect();
	if (!r || r.width === 0) return null;
	return {
		top: Math.round(r.bottom),
		right: Math.round(document.documentElement.clientWidth - r.right),
	};
}

/**
 * With an anchor on a wide screen with a mouse, where the sheet hangs as a popover under it;
 * null for the bottom sheet. Measured before the first paint, so it never starts as a sheet,
 * and again when the window changes size or crosses the breakpoint.
 */
function usePopoverPlace(anchor: (() => HTMLElement | null) | undefined): Place | null {
	const anchorRef = useRef(anchor);
	anchorRef.current = anchor;
	const [place, setPlace] = useState<Place | null>(() => placeUnder(anchor));
	const anchored = !!anchor;
	useLayoutEffect(() => {
		if (!anchored || !window.matchMedia) return;
		const measure = () =>
			setPlace((was) => {
				const now = placeUnder(anchorRef.current);
				return was && now && was.top === now.top && was.right === now.right ? was : now;
			});
		// Now, and again once the page has re-laid itself out for the new size (a screen that
		// switches layout in script moves ≡ a frame or two after the resize).
		let frame = 0;
		const update = () => {
			measure();
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => (frame = requestAnimationFrame(measure)));
		};
		measure();
		const mq = window.matchMedia(POPOVER_MEDIA);
		mq.addEventListener?.('change', update);
		window.addEventListener('resize', update);
		return () => {
			cancelAnimationFrame(frame);
			mq.removeEventListener?.('change', update);
			window.removeEventListener('resize', update);
		};
	}, [anchored]);
	return place;
}

export interface SheetProps {
	/** Controlled: the sheet is up while this is true. */
	open: boolean;
	/** Asked to close (✕, Escape, the dim or a click outside, a drag down, the phone's back). */
	onClose: () => void;
	/** Its heading, and the dialog's name. Required: a dialog needs a name. */
	title: ReactNode;
	/** A view inside the menu (How to play, Settings, About): ‹ goes back to the menu. */
	onBack?: (() => void) | undefined;
	children: ReactNode;
	className?: string | undefined;
	testId?: string | undefined;
	/** Where focus goes on close when the opener has gone, before the fallbacks. */
	returnTo?: (() => HTMLElement | null) | undefined;
	/** The button it opened from: on a wide screen with a mouse it hangs under it as a popover. */
	anchor?: (() => HTMLElement | null) | undefined;
	/**
	 * The phone's back closes it (`useOverlay`, app/router.tsx): its own history entry by
	 * default; `nested` for a view inside a sheet that's already open; false when the caller
	 * keeps the entry itself.
	 */
	history?: boolean | 'nested' | undefined;
	/**
	 * Drawn beside the sheet, over it, outside its scrolling box (the install guide's pointer at
	 * the browser's own button). Not part of the dialog: keep it decorative (aria-hidden).
	 */
	outside?: ReactNode;
	/**
	 * The sheet draws its own heading (the install guide: Nib beside a big title and its line):
	 * no heading row; ✕ floats in the corner, level with the heading. The caller must render
	 * `<SheetTitle>` (the dialog's name) in its body. `title` still keys "a new view: read from
	 * the top".
	 */
	bareHeader?: boolean | undefined;
	/** `data-*` attributes for the sheet (`.sheet`). */
	[data: `data-${string}`]: string | number | boolean | undefined;
}

/** The sheet's title, for a sheet that draws its own heading (`bareHeader`): names the dialog. */
export function SheetTitle({ className, children }: { className?: string; children: ReactNode }) {
	// No id of its own: Radix's id is what names the dialog (aria-labelledby).
	return <Dialog.Title className={className}>{children}</Dialog.Title>;
}

/**
 * What a sheet and its opener share: the sheet's way out (Radix asks for it when Escape, the
 * dim or a click outside closes the dialog) and the trigger, for focus to go back to.
 */
interface Hub {
	leave: { current: (() => void) | null };
	trigger: { current: HTMLElement | null };
	onOpen: { current: (() => void) | undefined };
}

function useHub(): Hub {
	const [hub] = useState<Hub>(() => ({ leave: { current: null }, trigger: { current: null }, onOpen: { current: undefined } }));
	return hub;
}

/** The SheetRoot a Sheet (or a SheetTrigger) sits in; null inside a sheet's own body. */
const RootContext = createContext<Hub | null>(null);

function DialogRoot({ hub, open, children }: { hub: Hub; open: boolean; children: ReactNode }) {
	return (
		<RootContext.Provider value={hub}>
			<Dialog.Root
				open={open}
				onOpenChange={(o) => {
					if (o) hub.onOpen.current?.();
					else hub.leave.current?.();
				}}
			>
				{children}
			</Dialog.Root>
		</RootContext.Provider>
	);
}

/**
 * The sheet's Radix root, for when its opener is a button: put the `SheetTrigger` and the
 * `Sheet` inside it (anywhere inside; one sheet per root). Mounted while the sheet is closed
 * too, so the trigger says what it opens (aria-haspopup="dialog", aria-expanded,
 * aria-controls, all from Radix) and focus goes back to it when the sheet closes. `open`
 * is the sheet's: keep the two the same. `onOpen` is called when the trigger is pressed (or
 * keep the button's own onClick).
 *
 *   <SheetRoot open={open}>
 *     <SheetTrigger><button onClick={() => setOpen(true)}>New collection</button></SheetTrigger>
 *     <Sheet open={open} onClose={…} title="New collection">…</Sheet>
 *   </SheetRoot>
 */
export function SheetRoot({ open, onOpen, children }: { open: boolean; onOpen?: (() => void) | undefined; children: ReactNode }) {
	const hub = useHub();
	hub.onOpen.current = onOpen;
	return (
		<DialogRoot hub={hub} open={open}>
			{children}
		</DialogRoot>
	);
}

/**
 * The button that opens a sheet (Radix Dialog.Trigger, `asChild`): inside a `SheetRoot`.
 * Its child is the caller's own button, which keeps its look and its onClick.
 */
export function SheetTrigger({ children }: { children: ReactElement }) {
	const hub = useContext(RootContext);
	if (!hub) throw new Error('SheetTrigger must be inside a SheetRoot');
	return (
		<Dialog.Trigger asChild ref={hub.trigger as RefObject<HTMLButtonElement | null>}>
			{children}
		</Dialog.Trigger>
	);
}

/**
 * The sheet (Radix Dialog): a bottom sheet over the page on phones (the dim, a handle, a
 * heading row with ✕, and ‹ back to the menu in a view opened from it), and, given an
 * `anchor` (≡), a ~400px popover under that button from 960px with a mouse (J4: no dim, no
 * drag). Controlled by `open`. Escape, the dim or a click outside, ✕ and a drag down by the
 * top call `onClose` after the way-out animation; the phone's back calls it at once.
 *
 * Its Radix root is always mounted: its own, or, inside a `SheetRoot`, that one (whose
 * `SheetTrigger` is then the opener). The body mounts as it opens.
 *
 * Radix traps focus, names it from the title and hides the rest of the page from a screen
 * reader; the kit adds: focus starts on the sheet itself (so a screen reader reads it from
 * the top), the page behind goes `inert`, and on close focus goes back to the opener, or, if
 * that's gone, to whatever opened the sheet it came from, then its `SheetTrigger`, then the
 * page's heading (`useDialogReturnFocus`; `returnTo` first). A toast raised while it's up is
 * read from inside it (`SheetSay`), since the page is inert.
 */
export function Sheet({ open, history = true, ...rest }: SheetProps) {
	useOverlay(open && history !== false, rest.onClose, {
		nested: history === 'nested',
	});
	const outer = useContext(RootContext);
	const own = useHub();
	const hub = outer ?? own;
	// A sheet opened from inside this one makes its own root.
	const body = open ? (
		<RootContext.Provider value={null}>
			<SheetBody hub={hub} {...rest} />
		</RootContext.Provider>
	) : null;
	if (outer) return body;
	return (
		<DialogRoot hub={own} open={open}>
			{body}
		</DialogRoot>
	);
}

/** The open sheet: mounts as it opens, so it reads its opener before anything moves focus. */
function SheetBody({ hub, title, onClose, onBack, children, className, testId, returnTo, anchor, outside, bareHeader, ...data }: Omit<SheetProps, 'open' | 'history'> & { hub: Hub }) {
	const ref = useRef<HTMLDivElement>(null);
	const closeRef = useRef(onClose);
	closeRef.current = onClose;
	// The page wakes before focus goes back into it (effects clean up in this order).
	useModalBackground();
	useDialogReturnFocus(ref, returnTo, () => hub.trigger.current);
	const leave = () => sheetExit(ref.current, () => closeRef.current());
	// Radix's own asks to close (Escape, the dim, a click outside) come through the root.
	useLayoutEffect(() => {
		const go = () => sheetExit(ref.current, () => closeRef.current());
		hub.leave.current = go;
		return () => {
			if (hub.leave.current === go) hub.leave.current = null;
		};
	}, [hub]);
	useLayer(true, leave);
	const drag = useSheetDrag(ref, leave);
	const place = usePopoverPlace(anchor);
	const popStyle = place
		? ({
				'--pop-top': `${place.top}px`,
				'--pop-right': `${place.right}px`,
			} as CSSProperties)
		: undefined;

	// Opening: a soft, low pop (a sheet is big, so it sounds lower than a button; game/sound.ts).
	useEffect(() => uiPop(ref.current), []);

	// A new view in the same sheet (menu → How to play): start reading it from the top.
	const firstTitle = useRef(true);
	useEffect(() => {
		if (firstTitle.current) {
			firstTitle.current = false;
			return;
		}
		ref.current?.scrollTo?.({ top: 0 });
		ref.current?.focus({ preventScroll: true });
	}, [title]);

	return (
		<>
			{/* React's own portal, not Radix's (which mounts a render later): the sheet is in the
			    page by the time its effects run, so useDialogReturnFocus can note who opened it. */}
			{createPortal(
				<div className={place ? 'sheet-wrap pop' : 'sheet-wrap'}>
					{/* forceMount: mounted is open (this body only mounts while the sheet is). */}
					<Dialog.Overlay forceMount className="sheet-dim" />
					<Dialog.Content
						forceMount
						{...data}
						ref={ref}
						className={className ? `sheet ${className}` : 'sheet'}
						aria-modal="true"
						aria-describedby={undefined}
						data-testid={testId}
						style={popStyle}
						onOpenAutoFocus={(e) => {
							// The sheet itself, not its first button: read from the top.
							e.preventDefault();
							ref.current?.focus({ preventScroll: true });
						}}
						// Focus goes back through useDialogReturnFocus, which knows the fallbacks.
						onCloseAutoFocus={(e) => e.preventDefault()}
						onEscapeKeyDown={(e) => {
							// Nothing under the sheet (the game's keys) hears this Escape.
							e.stopPropagation();
						}}
					>
						{bareHeader ? (
							<>
								<div className="sh-grab" {...(place ? {} : drag)}>
									<div className="handle" aria-hidden="true" />
								</div>
								<button type="button" className="x x-float" aria-label="Close" onClick={leave}>
									<InstallIcon.close />
								</button>
							</>
						) : (
						<div className="sh-grab" {...(place ? {} : drag)}>
							<div className="handle" aria-hidden="true" />
							<div className="sh-top">
								{onBack && (
									<button type="button" className="x sh-back" aria-label="Back to the menu" onClick={onBack}>
										<Icon.chevron />
									</button>
								)}
								<Dialog.Title className="sh-h">{title}</Dialog.Title>
								<button type="button" className="x" aria-label="Close" onClick={leave}>
									<InstallIcon.close />
								</button>
							</div>
						</div>
						)}
						{children}
						<SheetSay />
					</Dialog.Content>
					{outside}
				</div>,
				document.body,
			)}
		</>
	);
}
